import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useEffect, useState } from "react";
import { useDeviceData } from "react-device-detect";
import { parseEther } from "viem";
import {
  useAccount,
  useBalance,
  useSwitchChain,
  useWaitForTransactionReceipt,
} from "wagmi";
import { writeContract } from "wagmi/actions";

import { Badge } from "@/components/ui/badge";

import RaceResult from "../abi/RaceResult";
import { useIsCollector } from "../hooks/useIsCollector";
import { Button } from "./Button";
import { config } from "./EthereumProviders";

const MintRow = ({
  elapsed,
  seed,
  theme,
  attempts,
  deviceModel,
  rank,
  personalBest,
}: {
  elapsed: number;
  seed: string;
  theme: any;
  attempts: number;
  deviceModel: string;
  rank: number;
  personalBest: boolean;
}) => {
  const [hash, setHash] = useState<`0x${string}`>();
  const [minted, setMinted] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const { address, chain, isConnected } = useAccount();
  const chainId = chain?.id;
  const { switchChain } = useSwitchChain();
  const { data: balanceData } = useBalance({ address, chainId: 8453 });

  const { isCollector } = useIsCollector({
    contractAddresses: [],
  });

  const postResult = async () => {
    try {
      setIsMinting(true);
      const svgElement = document.getElementById("racePath");

      // Convert to base64 Data URL
      const svgMarkup = new XMLSerializer().serializeToString(svgElement);
      const base64Encoded = btoa(unescape(encodeURIComponent(svgMarkup)));
      const trackImage = "data:image/svg+xml;base64," + base64Encoded;

      const res = await fetch("/api/pin-and-sign", {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Accept: "application/json",
        }),
        body: JSON.stringify({
          track: seed,
          timeInSeconds: elapsed,
          timeInMilliseconds: elapsed * 1000,
          // pathPoints,
          trackImage,
          theme,
          sender: address,
          attempts,
          deviceModel,
        }),
      });

      const body = await res.json();
      writeContract(config, {
        ...RaceResult,
        functionName: "mintWithProof",
        value: parseEther("0.0008"),
        args: [body.signature, body.raceResult],
        onError: (e) => {
          console.log(e);
        },
      })
        .then((txData) => {
          setHash(txData);
          setIsMinting(false);
        })
        .catch((e) => {
          console.log(e);
          setIsMinting(false);
        });
    } catch (e) {
      console.log(e);
      setIsMinting(false);
    }
  };

  const { data, isError, isLoading } = useWaitForTransactionReceipt({
    hash,
  });

  useEffect(() => {
    if (data?.status === "success") {
      setMinted(true);
    }
  }, [data]);

  const helperCopy = () => {
    if (isMinting) return "Wait while we prepare your Race Result NFT";
    if (isLoading) return "Race Result NFT is being minted";
    if (minted) return "Race Result NFT minted!";
    return `Mint your Race Result ${
      rank < 10 ? "to join the leaderboard" : ""
    }`;
  };

  const MintButton = () => {
    if (!isConnected) return <ConnectButton />;
    if (chainId !== RaceResult.chainId)
      return (
        <Button
          onClick={() => switchChain({ chainId: RaceResult.chainId })}
          theme={theme}
          pending={isMinting}
        >
          Switch Chain
        </Button>
      );
    if (!isCollector)
      return (
        <p className="global-shadow text-white font-Bebas text-lg text-center">
          Minting is restricted to certain addresses
        </p>
      );

    if (minted) return null;
    if (balanceData?.value < parseEther("0.0008"))
      return (
        <a
          target="_blank"
          href="https://bridge.base.org/deposit"
          className="text-xs text-blue"
          rel="noreferrer"
        >
          Bridge to Base
        </a>
      );

    return (
      <Button
        onClick={postResult}
        theme={theme}
        pending={isMinting || isLoading}
        disabled={minted}
      >
        Mint for 0.0008 ETH
      </Button>
    );
  };

  return (
    <div
      className="relative flex flex-row w-full align-center justify-start mt-4"
      style={{
        width: "calc(100dvh * 0.616)",
        maxWidth: "100vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 0,
      }}
    >
      <div
        className="w-full flex flex-row justify-start items-center p-2 space-x-2"
        style={{
          background: theme.background,
        }}
      >
        <div className="flex flex-col items-center font-Bebas basis-3/5 flex-grow">
          <p className="global-shadow flex-grow text-white  text-lg text-center">
            {helperCopy()}
          </p>
          <div className="flex flex-row items-center justify-center space-x-2">
            <Badge variant="secondary" className="text-center">
              {rank < 10 ? "🏆" : "🏅"} #{rank} Global Rank
            </Badge>
            {personalBest ? (
              <Badge variant="secondary" className="text-center">
                🏆 New Personal Best
              </Badge>
            ) : (
              <Badge variant="destructive" className="text-center">
                Not your best time
              </Badge>
            )}
          </div>
        </div>
        <MintButton />
      </div>
    </div>
  );
};

export default MintRow;
